import { API_URL } from 'config'
import { getCurrentAccessToken } from './authSession'

const defaultHeaders = {
  accept: 'application/json',
  'content-type': 'application/json'
}

async function buildHeaders(args) {
  const headers = {
    ...defaultHeaders,
    ...(args.headers || {})
  }

  if (args.auth !== false) {
    const accessToken = await getCurrentAccessToken()

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    } else {
      delete headers.Authorization
    }
  }

  return headers
}

const addRequestContext = (error, path, method, response) => {
  error.requestMethod = method
  error.requestPath = path

  // Only CORS-safelisted response headers are readable from JS, and the APIs send
  // no Access-Control-Expose-Headers, so this stays undefined today even though
  // API Gateway does send x-amzn-RequestId. It starts working once the API adds
  // `Access-Control-Expose-Headers: x-amzn-RequestId,x-amz-apigw-id`; until then,
  // do not rely on it when correlating a frontend error with CloudWatch.
  const requestId =
    response?.headers?.get?.('x-amzn-requestid') || response?.headers?.get?.('x-amz-request-id')

  if (requestId) {
    error.requestId = requestId
  }

  return error
}

const buildRequestError = (res, body, path, method) => {
  const fallbackMessage =
    res.status === 401 ? 'Unauthorized' : res.status === 403 ? 'Forbidden' : res.statusText
  const error = new Error(body?.message || fallbackMessage)

  error.status = res.status
  error.statusText = res.statusText
  error.responseBody = body

  if (body?.errorCode) {
    error.errorCode = body.errorCode
  }

  return addRequestContext(error, path, method, res)
}

// Concurrent requests share one session lookup, so a rejection is a single Error
// handed to all of them. Annotating that object in place would have every caller
// report whichever request mutated it last, so each one annotates its own copy and
// keeps the original as the cause.
const copySharedError = error => {
  if (!(error instanceof Error)) {
    return new Error(String(error))
  }

  const copy = new Error(error.message)

  copy.name = error.name
  copy.stack = error.stack
  // Own enumerable properties only; Amplify attaches its own (recoverySuggestion,
  // underlyingError) and message/stack are not enumerable, so they are not clobbered.
  Object.assign(copy, error)
  copy.cause = error

  return copy
}

const buildRequestOptions = async (path, args) => {
  const method = args.method || 'GET'
  let headers

  try {
    headers = await buildHeaders(args)
  } catch (error) {
    // The token lookup can fail on its own, typically a network blip while Amplify
    // refreshes. That rejection never reaches the fetch chain below, so without
    // this the one failure this layer exists to explain is the one that arrives
    // with no method or path attached.
    throw addRequestContext(copySharedError(error), path, method)
  }

  const options = { headers, method }

  if (args.data) {
    options.body = JSON.stringify(args.data)
    options.headers['content-type'] = 'application/json'
  }

  return options
}

export default async function request(path, args = {}) {
  const url = `${API_URL}/${path}`

  const options = await buildRequestOptions(path, args)
  const allowStatuses = args.allowStatuses || []
  return fetch(url, options)
    .then(async res => {
      if (res.status >= 200 && res.status < 300) {
        return res
      }

      if (allowStatuses.includes(res.status)) {
        return res
      }

      return res
        .json()
        .catch(() => null)
        .then(body => {
          throw buildRequestError(res, body, path, options.method)
        })
    })
    .then(res => {
      // Some callers assume every successful request resolves to the same [body, response] array shape.
      // A 204 has no JSON body, but it is still a successful "empty result" response, so we
      // normalize it to [null, response] instead of returning the raw Response object.
      //
      // Long term, this request layer should move away from this array-based response shape and expose
      // a clearer named shape (for example { data, response }) for all successful requests.
      if (res.status === 204) {
        return [null, res]
      }

      return res.json().then(body => [body, res])
    })
    .catch(error => {
      // Last in the chain on purpose: a .catch only sees the links above it, so from here
      // it also covers a successful response whose body turns out not to be valid JSON.
      throw addRequestContext(error, path, options.method)
    })
}

export async function requestFile(path, args = {}) {
  const url = `${API_URL}/${path}`

  const options = await buildRequestOptions(path, args)

  return fetch(url, options)
    .then(async res => {
      if (res.status >= 200 && res.status < 300) {
        return res
      }

      const body = await res.json().catch(() => null)
      throw buildRequestError(res, body, path, options.method)
    })
    .catch(error => {
      throw addRequestContext(error, path, options.method)
    })
}
